import React from 'react';
import {
    Box,
    Button,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AddIcon from '@mui/icons-material/Add';
import { CommunicationSearchItem } from '../../../hooks/Marketing';
import { Store } from '../../../types/Store';
import {
    formatDateTime,
    getChannelOption,
    getCommunicationStatusInfo,
    getCommunicationType,
    getStoreLabel,
    isLocalCommunication,
} from './constants';

interface CommunicationResultTableProps {
    communications: CommunicationSearchItem[];
    stores: Store[] | null;
    onDelete: (id: number) => void;
    showCreateCommunicationButton?: boolean;
}

const COLUMN_COUNT = 9;

const CommunicationResultTable = ({
    communications,
    stores,
    onDelete,
    showCreateCommunicationButton = false,
}: CommunicationResultTableProps) => (
    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, backgroundColor: 'background.paper' }}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Campagne</TableCell>
                    <TableCell>Canal</TableCell>
                    <TableCell>Début d&apos;utilisation</TableCell>
                    <TableCell>Fin d&apos;utilisation</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Magasin</TableCell>
                    <TableCell align="right">Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {communications.map((communication) => {
                    const channel = getChannelOption(communication.defChannelId);
                    const ChannelIcon = channel.icon;
                    const status = getCommunicationStatusInfo(communication);
                    const isLocal = isLocalCommunication(communication.entityId, stores);

                    return (
                        <TableRow key={communication.id} hover>
                            <TableCell>#{communication.id}</TableCell>
                            <TableCell>
                                <Typography variant="body2" fontWeight={600} noWrap title={communication.defTitle} sx={{ maxWidth: 220 }}>
                                    {communication.defTitle}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ChannelIcon fontSize="small" titleAccess={channel.label} sx={{ color: 'text.secondary' }} />
                                    <Typography variant="body2">{channel.label}</Typography>
                                </Box>
                            </TableCell>
                            <TableCell>{formatDateTime(communication.defSendStart)}</TableCell>
                            <TableCell>{formatDateTime(communication.defSendEnd)}</TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                    {status.isTerminated && (
                                        <Chip label="Envoyée" size="small" color="success" sx={{ fontSize: '0.7rem', height: 20 }} />
                                    )}
                                    {status.isExtractionInProgress && (
                                        <Chip label="Extraction en cours" size="small" color="warning" sx={{ fontSize: '0.7rem', height: 20 }} />
                                    )}
                                    {status.isExtracted && (
                                        <Chip label="Extraite" size="small" color="success" sx={{ fontSize: '0.7rem', height: 20 }} />
                                    )}
                                    {status.isTestOperation && (
                                        <Chip label="Opération de test" size="small" color="info" sx={{ fontSize: '0.7rem', height: 20 }} />
                                    )}
                                    {status.isModel && (
                                        <Chip label="Modèle" size="small" color="default" sx={{ fontSize: '0.7rem', height: 20 }} />
                                    )}
                                    {status.isWaitingValidation && status.remaining && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AccessTimeIcon fontSize="small" sx={{ color: status.isExpired ? 'error.main' : 'success.main' }} />
                                            <Typography variant="caption" sx={{ color: status.isExpired ? 'error.main' : 'success.main', fontWeight: 600 }}>
                                                {status.remaining.label}
                                            </Typography>
                                        </Box>
                                    )}
                                    {!status.isTerminated &&
                                        !status.isExtractionInProgress &&
                                        !status.isExtracted &&
                                        !status.isTestOperation &&
                                        !status.isModel &&
                                        !status.isWaitingValidation && (
                                            <Chip label="Brouillon" size="small" color="default" sx={{ fontSize: '0.7rem', height: 20 }} />
                                        )}
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocationOnIcon fontSize="small" sx={{ color: isLocal ? 'info.main' : 'error.main' }} />
                                    <Typography variant="body2">{getCommunicationType(communication.entityId, stores)}</Typography>
                                </Box>
                            </TableCell>
                            <TableCell>{getStoreLabel(communication.entityId, stores)}</TableCell>
                            <TableCell align="right">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap' }}>
                                    {showCreateCommunicationButton && (
                                        <Button size="small" variant="outlined" startIcon={<AddIcon fontSize="small" />}>
                                            Créer la campagne
                                        </Button>
                                    )}
                                    {status.canView && (
                                        <Button size="small" variant="outlined" startIcon={<VisibilityIcon fontSize="small" />}>
                                            Visualiser
                                        </Button>
                                    )}
                                    {status.canFinalize && (
                                        <Button size="small" variant="contained">
                                            Finaliser
                                        </Button>
                                    )}
                                    {status.canDuplicate && (
                                        <Button size="small" variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />}>
                                            Dupliquer
                                        </Button>
                                    )}
                                    {status.canDelete && (
                                        <Button
                                            size="small"
                                            color="error"
                                            variant="outlined"
                                            startIcon={<DeleteOutlineIcon fontSize="small" />}
                                            onClick={() => onDelete(communication.id)}
                                        >
                                            Supprimer
                                        </Button>
                                    )}
                                </Box>
                            </TableCell>
                        </TableRow>
                    );
                })}
                {communications.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={COLUMN_COUNT} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                Aucune campagne trouvée
                            </Typography>
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    </TableContainer>
);

export default CommunicationResultTable;
